import PageHeader from "components/Page/PageHeader";
import { Flex } from "@chakra-ui/layout";
import { useState, useCallback, useEffect, useMemo } from "react";
import ListingPageHeaderBar from "ui/components/Page/ListingPageHeaderBar";
import useFiltroRelatorioReceitas from "../../hooks/useFiltroRelatorioReceitas";
import { useCurrentTenant } from "features/auth/tenant.store";
import ModalFiltrosRelatorioReceitas from "./../ModalFiltrosRelatorioReceitas/index";
import TagStatusFiltroRelatoriosReceita from "./../TagStatusFiltroRelatoriosReceita/index";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import moment from "moment";
import { exportExcelRelatorioReceitas } from "./../util/helper";
import useRelatorioReceitasPdf from "core/features/relatorios/hooks/useRelatorioReceitasPdf";
import PDFRelatorioReceitas from "./../PDFRelatorio/index";
import ExportMenuReceitas from "./../ExportMenuReceitas/index";
import { RelatorioReceitasFields } from "core/features/relatorios/typings";
import RelatorioReceitasContent from "./../RelatorioReceitasContent/index";
import useRelatorioReceitasInfiniteScroll from "core/features/relatorios/hooks/useRelatorioReceitasInfiniteScroll";
import { RelatorioReceitasItem } from "core/features/relatorios/typings/index";
import useRelatorioReceitasTotais from "core/features/relatorios/hooks/useRelatorioReceitasTotais";
import { AssinaturasItem } from "components/Relatorio/FormAssinaturas";

function ListagemReceitas() {
  const { filtro, onChangeFiltro, onResetFiltro } =
    useFiltroRelatorioReceitas();

  const {
    data: dataReceitas,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRelatorioReceitasInfiniteScroll(filtro);

  const {
    data: totais,
    isLoading: isLoadingTotais,
    isFetching: isFetchingTotais,
  } = useRelatorioReceitasTotais(filtro, !isLoading);

  const [isOpenModalFiltro, setIsOpenModalFiltro] = useState(false);
  const [modalSelectFieldsOpen, setModalSelectFieldsOpen] = useState(false);
  const [exportType, setExportType] = useState<
    { type: "pdf" | "excel" } | undefined
  >();
  const { tenant } = useCurrentTenant();

  const { isLoading: isLoadingPdf, mutate: handleRelatorioPdf } =
    useRelatorioReceitasPdf(filtro);

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: `Relatório de Receitas - ${moment().format("DD_MM_YYYY")}`,
    Component: PDFRelatorioReceitas,
    loading: isLoadingPdf,
  });

  const _onExportPdf = useCallback(
    async (
      selectedFields: RelatorioReceitasFields[],
      assinaturas?: AssinaturasItem[]
    ) => {
      handleRelatorioPdf({} as any, {
        onSuccess: (data) => {
          onExportPdf({
            data: { ...data, assinaturas },
            renderUnidade: selectedFields.includes("unidade"),
            renderProprietario: selectedFields.includes("nomeCondomino"),
            renderResumoCategorias: selectedFields.includes(
              "resumoContaCategoria"
            ),
            fields: selectedFields,
          });
        },
      });
    },
    [handleRelatorioPdf, onExportPdf]
  );

  const _onExportExcel = useCallback(
    async (selectedFields: RelatorioReceitasFields[]) => {
      handleRelatorioPdf({} as any, {
        onSuccess: (data) => {
          exportExcelRelatorioReceitas({
            data,
            selectedFields,
          });
        },
      });
    },
    [handleRelatorioPdf]
  );

  useEffect(() => {
    if (exportType) {
      setModalSelectFieldsOpen(true);
    }
  }, [exportType]);

  const numeroFiltroSelecionados =
    (filtro.contasBancariasIds?.length ? 1 : 0) +
    (filtro.centrosDeCustoIds?.length ? 1 : 0) +
    (filtro.contasCategoriasIds?.length ? 1 : 0) +
    (filtro.dataInicio || filtro.dataFim ? 1 : 0) +
    (filtro.tipoData ? 1 : 0) +
    (filtro.unidadesIds?.length ? 1 : 0);

  const receitas = useMemo(() => {
    return (
      dataReceitas?.pages.reduce(
        (agg: RelatorioReceitasItem[], item) => agg.concat(item.items ?? []),
        []
      ) ?? []
    );
  }, [dataReceitas]);

  return (
    <>
      <PageHeader
        justifyContent="space-between"
        title="Relatório de receitas"
      />

      <ListingPageHeaderBar
        valueSearch={filtro.search}
        onSearch={(search) => onChangeFiltro({ search })}
        visibleFiltro
        badge
        badgeValue={numeroFiltroSelecionados}
        onFiltro={() => setIsOpenModalFiltro(true)}
        children={
          <Flex justifyContent="space-between" w="100%">
            <TagStatusFiltroRelatoriosReceita
              filtro={filtro}
              onChangeFiltro={onChangeFiltro}
            />

            <ExportMenuReceitas
              visible={modalSelectFieldsOpen}
              onClose={() => setModalSelectFieldsOpen(false)}
              onExportExcel={_onExportExcel}
              onExportPdf={_onExportPdf}
              setExportType={setExportType}
              exportType={exportType}
            />
          </Flex>
        }
      />

      <RelatorioReceitasContent
        data={receitas}
        isFetchingNextPage={isFetchingNextPage}
        totais={totais}
        isLoading={isLoading}
        fetchNextPage={fetchNextPage}
        hasNextPage={!!hasNextPage}
        isLoadingTotais={isLoadingTotais || isFetchingTotais || isLoading}
      />

      <ModalFiltrosRelatorioReceitas
        tenantId={tenant.id}
        filtro={filtro}
        onChangeFiltro={onChangeFiltro}
        onReset={onResetFiltro}
        visible={isOpenModalFiltro}
        onClose={() => setIsOpenModalFiltro(false)}
      />
    </>
  );
}

export default ListagemReceitas;
